import { supabase } from '@/utils/supabase';
import { featureFlagService } from '@/services/featureFlagService';
import { RECON_FEE_ACCOUNTING_FLAG, type ReconFeeAccountingSnapshot } from '@/utils/banker-recon/feePaymentLedger';
import type { ReconDocFeeRow } from '../../supabase/functions/_shared/banker-recon-doc-fees';
import type { EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';

export interface ReconFeeContext {
  accounting: ReconFeeAccountingSnapshot;
  loanPatch: Partial<EncompassLoan>;
  docRows: ReconDocFeeRow[];
}

/** The server independently checks the flag and loan access on every invocation. */
export async function refreshBankerReconFees(encompassLoanId: string, userId: string, required = false): Promise<ReconFeeContext | null> {
  featureFlagService.invalidateGenericCache(RECON_FEE_ACCOUNTING_FLAG);
  const enabled = await featureFlagService.isFlagEnabled(RECON_FEE_ACCOUNTING_FLAG, userId);
  if (!enabled && !required) {
    return null;
  }
  const { data, error } = await supabase.functions.invoke('banker-recon-fee-capture', { body: { encompassLoanId } });
  if (error || data?.error) {
    throw new Error(data?.error || 'Could not verify lender-paid fees. Refresh before saving or finalizing.');
  }
  if (!data?.enabled) {
    if (required) {
      throw new Error('Lender-paid fee accounting is disabled. This saved draft remains read-only.');
    }
    return null;
  }
  if (data.capture?.status !== 'complete' || data.capture?.version !== 1
    || !data.docOrderSignature || !Array.isArray(data.docRows) || !Array.isArray(data.issues) || !data.loanPatch) {
    throw new Error('Incomplete fee capture. Refresh before saving or finalizing.');
  }
  return {
    accounting: {
      version: 1,
      capture: data.capture,
      docOrderSignature: data.docOrderSignature,
      lenderCreditTimingBasis: 'total',
      retainedClosingFeeBasis: 'expandedLeFees',
      issues: data.issues
    },
    loanPatch: data.loanPatch,
    docRows: data.docRows
  };
}
